import { Box, Button, Card, Group, Select, Stack, Stepper, Text, TextInput } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IconPlus } from "@tabler/icons-react";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useSWR from "swr";
import { ChargeContractApi, TenantApi } from "../../../apis";
import type { ComponentInput } from "../../../apis/chargeContract";
import { ComponentEditor } from "../../../components/billing/ComponentEditor";
import { componentTypeLabel } from "../../../components/billing/ComponentTypeIcon";
import { PageLayout } from "../../../components/layout";
import { formatDate } from "../../../utils/format";

const CURRENCIES = ["MNT", "USD", "EUR", "CNY", "JPY", "KRW"];

interface TenantRow {
  id: string;
  name: string;
}

const CreateWizard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: tenantsRaw } = useSWR("swr.tenants.list", () => TenantApi.list());
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tenants: TenantRow[] = Array.isArray(tenantsRaw) ? (tenantsRaw as TenantRow[]) : ((tenantsRaw as any)?.rows ?? []);
  const [active, setActive] = useState(0);

  // Step 1
  const [tenantId, setTenantId] = useState<string>(searchParams.get("tenantId") ?? "");
  const [contractName, setContractName] = useState<string>("");
  const [currency, setCurrency] = useState<string>("MNT");
  const todayIso = new Date().toISOString().substring(0, 10);
  const [effectiveFrom, setEffectiveFrom] = useState<string | null>(todayIso);

  // Step 2
  const [components, setComponents] = useState<ComponentInput[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const breadcrumbs = [
    { to: "/", label: "Эхлэл" },
    { to: "/billing/contracts", label: "Гэрээ" },
    { label: "Шинээр" }
  ];

  const canStep1 = !!tenantId && !!contractName && !!currency && !!effectiveFrom;
  const canStep2 = components.length > 0 && components.every((c) => (c.label ?? "").trim().length > 0);

  const addComponent = (type: ComponentInput["componentType"]) => {
    if (components.some((c) => c.componentType === type)) return;
    setComponents([...components, { componentType: type, currency }]);
  };

  const handleSubmit = async () => {
    if (!effectiveFrom) return;
    setBusy(true);
    setError(null);
    try {
      const result = await ChargeContractApi.create({
        tenantId,
        contractName,
        currency,
        effectiveFrom: new Date(effectiveFrom).toISOString(),
        components
      });
      navigate(`/billing/contracts/${result.id}`);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageLayout title="Шинэ гэрээ үүсгэх" subTitle="Платформ ↔ тенантын төлбөрийн гэрээ" breadcrumb={breadcrumbs}>
      <Stack gap="lg">
        <Stepper active={active} onStepClick={setActive}>
          <Stepper.Step
            label="Тенант + үндсэн"
            description="Үндсэн мэдээлэл"
            allowStepSelect={canStep1 || active >= 1}
          >
            <Card withBorder radius="md" padding="lg" mt="lg">
              <Stack gap="md">
                <Select
                  label="Тенант"
                  placeholder="Тенант сонгох"
                  required
                  searchable
                  clearable
                  nothingFoundMessage="Тенант олдсонгүй"
                  data={tenants.map((t) => ({ value: t.id, label: t.name }))}
                  value={tenantId}
                  onChange={(v) => setTenantId(v ?? "")}
                />
                <TextInput
                  label="Гэрээний нэр"
                  placeholder="Жишээ: Acme сарын төлбөр 2026"
                  required
                  value={contractName}
                  onChange={(e) => setContractName(e.currentTarget.value)}
                />
                <Select
                  label="Валют"
                  required
                  data={CURRENCIES.map((c) => ({ value: c, label: c }))}
                  value={currency}
                  onChange={(v) => setCurrency(v ?? "MNT")}
                  w={200}
                />
                <DateInput
                  label="Үйлчилгээ эхлэх огноо"
                  placeholder="Огноо сонгох"
                  required
                  valueFormat="YYYY-MM-DD"
                  value={effectiveFrom}
                  onChange={(v) => setEffectiveFrom(v)}
                />
              </Stack>
            </Card>
          </Stepper.Step>

          <Stepper.Step
            label="Бүрэлдэхүүн"
            description="Component-ууд"
            allowStepSelect={canStep2 || active >= 2}
          >
            <Stack gap="md" mt="lg">
              <Group gap="xs">
                <Text size="sm" c="dimmed">Нэмэх:</Text>
                {(["RECURRING_SUBSCRIPTION", "ONE_TIME_PURCHASE", "TRANSACTION_FEE", "FREE_OR_CUSTOM", "END_CUSTOMER_RECURRING"] as ComponentInput["componentType"][]).map(
                  (t) => (
                    <Button
                      key={t}
                      size="xs"
                      variant="default"
                      leftSection={<IconPlus size={14} />}
                      onClick={() => addComponent(t)}
                      disabled={components.some((c) => c.componentType === t)}
                    >
                      {componentTypeLabel(t)}
                    </Button>
                  )
                )}
              </Group>
              {components.map((c, i) => (
                <ComponentEditor
                  key={i}
                  value={c}
                  currencyOptions={CURRENCIES}
                  tenantId={tenantId || undefined}
                  onChange={(next) => setComponents(components.map((x, j) => (j === i ? next : x)))}
                  onRemove={() => setComponents(components.filter((_, j) => j !== i))}
                />
              ))}
              {components.length === 0 && (
                <Card withBorder padding="md">
                  <Text c="dimmed" ta="center">Дор хаяж нэг бүрэлдэхүүн нэмэх шаардлагатай.</Text>
                </Card>
              )}
            </Stack>
          </Stepper.Step>

          <Stepper.Step
            label="Шалгах"
            description="Үүсгэх"
            allowStepSelect={canStep1 && canStep2}
          >
            <Stack gap="sm" mt="lg">
              <Card withBorder padding="md">
                <Stack gap="xs">
                  <Group gap="lg">
                    <Text size="sm" c="dimmed">Тенант:</Text>
                    <Text>{tenants.find((t) => t.id === tenantId)?.name ?? tenantId}</Text>
                  </Group>
                  <Group gap="lg">
                    <Text size="sm" c="dimmed">Гэрээ:</Text>
                    <Text>{contractName}</Text>
                  </Group>
                  <Group gap="lg">
                    <Text size="sm" c="dimmed">Валют:</Text>
                    <Text>{currency}</Text>
                  </Group>
                  <Group gap="lg">
                    <Text size="sm" c="dimmed">Эхлэх:</Text>
                    <Text>{effectiveFrom ? formatDate(effectiveFrom) : ""}</Text>
                  </Group>
                  <Group gap="lg">
                    <Text size="sm" c="dimmed">Бүрэлдэхүүн:</Text>
                    <Text>{components.map((c) => componentTypeLabel(c.componentType)).join(", ")}</Text>
                  </Group>
                </Stack>
              </Card>
              {error && <Box c="red">{error}</Box>}
            </Stack>
          </Stepper.Step>
        </Stepper>

        <Group justify="flex-end">
          {active > 0 && (
            <Button variant="default" onClick={() => setActive(active - 1)} disabled={busy}>
              Буцах
            </Button>
          )}
          {active === 0 && (
            <Button onClick={() => setActive(1)} disabled={!canStep1}>
              Үргэлжлүүлэх
            </Button>
          )}
          {active === 1 && (
            <Button onClick={() => setActive(2)} disabled={!canStep2}>
              Үргэлжлүүлэх
            </Button>
          )}
          {active === 2 && (
            <Button color="teal" onClick={handleSubmit} disabled={busy || !canStep1 || !canStep2}>
              Үүсгэх (DRAFT)
            </Button>
          )}
        </Group>
      </Stack>
    </PageLayout>
  );
};

export default CreateWizard;
